import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Rocket, Users2, ShieldCheck, LineChart } from 'lucide-react';
import ScrollReveal from '@/components/ScrollReveal';

gsap.registerPlugin(ScrollTrigger);

const features = [
  {
    icon: Rocket,
    title: 'Production-Grade from Day One',
    description:
      'Most AI initiatives fail when they meet production reality: data boundaries, compliance rules, messy workflows, operational ownership. We design for these constraints from the start.',
    large: true,
  },
  {
    icon: Users2,
    title: 'Founder-Led Delivery',
    description:
      'You work directly with senior founders, not junior consultants learning on your project.',
    large: false,
  },
  {
    icon: ShieldCheck,
    title: 'Governance Built-In',
    description:
      'Security, audit trails, source grounding, and policy controls aren\'t afterthoughts — they\'re core to our platform design.',
    large: false,
  },
  {
    icon: LineChart,
    title: 'Measurable Outcomes',
    description:
      'We focus on concrete, measurable wins: cycle time reduction, operational efficiency, decision velocity improvements.',
    large: false,
  },
];

export default function WhyUs() {
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const cards = cardsRef.current?.querySelectorAll('.feature-card');
      if (cards) {
        gsap.fromTo(
          cards,
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
            stagger: 0.15,
            ease: 'expo.out',
            scrollTrigger: {
              trigger: cardsRef.current,
              start: 'top 80%',
            },
          }
        );
      }
    }, cardsRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className="section-padding bg-slate-50">
      <div className="container-custom">
        {/* Section Header */}
        <ScrollReveal className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Why CoDRIVER LABS
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            What makes us different from traditional AI consultancies
          </p>
        </ScrollReveal>

        {/* Features Grid */}
        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {features.map((feature, index) => (
            <div
              key={index}
              className="feature-card group relative bg-white rounded-xl p-8 border border-slate-200 shadow-card overflow-hidden transition-all duration-500 hover:shadow-card-hover flex flex-col"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              <div className="relative z-10 flex flex-col flex-1">
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary/20 transition-colors">
                  <feature.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
